/**
 * Notice handlers — one quiet line in a task that is neither the model's words
 * nor a step.
 *
 * Two frames carry one. PERMISSION_DENIED: the gate refused a step and the relay
 * composed the person's line onto the frame as `notice` (the frame's own
 * `reason` is written for the model and is never drawn). TASK_NOTICE: the
 * runner's own line, such as a plan heads-up. Both land as the SAME `notice`
 * block the server persists with the turn, so a reload shows what the live
 * stream showed.
 *
 * THE PARSE IS THE GATE (the reach precedent): a line that does not match the
 * shared schema is dropped, never repaired. One `ref` is one line in a task.
 */

import type { Socket } from "socket.io-client";
import {
  EVENTS,
  TaskNoticePayloadSchema,
  type NoticeBlock,
  type TaskNoticePayload,
} from "@bash/shared";
import { useTaskStore } from "../taskStore";

export function pushNotice(notice: TaskNoticePayload): void {
  const block: NoticeBlock = {
    type: "notice",
    tone: notice.tone,
    text: notice.text,
    ref: notice.ref,
  };
  useTaskStore.getState()._mutateTasks((tasks) => {
    const task = tasks.get(notice.taskId);
    if (!task) return;
    const known = [
      ...(task.streamingMessage?.content ?? []),
      ...task.messages.flatMap((m) => m.content),
    ];
    if (known.some((b) => b.type === "notice" && b.ref === notice.ref)) return;
    if (task.streamingMessage) {
      // Where it happened: between the steps it sits among.
      task.streamingMessage.content.push(block);
      return;
    }
    const clientId = `msg-notice-${notice.ref}`;
    task.messages = [
      ...task.messages,
      {
        id: clientId,
        role: "assistant" as const,
        content: [block],
        timestamp: new Date(),
        agentSlug: task.agentSlug || "bash",
        identity: { kind: "optimistic", clientId },
      },
    ];
  });
}

export function registerNoticeHandlers(socket: Socket): () => void {
  const handleDenied = (data: unknown) => {
    const parsed = TaskNoticePayloadSchema.safeParse(
      (data as { notice?: unknown } | null)?.notice
    );
    if (parsed.success) pushNotice(parsed.data);
  };
  const handleNotice = (data: unknown) => {
    const parsed = TaskNoticePayloadSchema.safeParse(data);
    if (parsed.success) pushNotice(parsed.data);
  };

  socket.on(EVENTS.PERMISSION_DENIED, handleDenied);
  socket.on(EVENTS.TASK_NOTICE, handleNotice);

  return () => {
    socket.off(EVENTS.PERMISSION_DENIED, handleDenied);
    socket.off(EVENTS.TASK_NOTICE, handleNotice);
  };
}
